import { useApolloClient, useMutation } from '@apollo/client';
import { Button, Radio, RadioGroup, Stack, Text, useNotifications } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import React, { useState } from 'react';

import { CompaniesDropdownContainer, CompanySearchResult } from '../../company/containers/companies_dropdown_container';
import {
  CompanyRelationshipType,
  CreateCompanyRelationshipMutation,
  CreateCompanyRelationshipMutationVariables,
} from '../../lib/graphql';
import { createCompanyRelationshipMutation } from '../../relationships/containers/relationship_mutations_container';
import { CompanyGroupRole, fetchCompanyGroupRole } from '../fetch_relationships';
import { CompanyGroupLinkConfirmModal } from './company_group_link_confirm_modal';

type Mode = 'new_hub' | 'existing_hub';

interface PendingLink {
  sourceCompanyId: string;
  targetCompanyId: string;
  memberName: string;
  hubName: string;
}

interface CompanyGroupFormProps {
  companyId: string;
  companyName: string;
  role: Exclude<CompanyGroupRole, 'member'>;
  onCreated: () => void;
}

export const CompanyGroupForm: React.FC<CompanyGroupFormProps> = ({ companyId, companyName, role, onCreated }) => {
  const apolloClient = useApolloClient();
  const notify = useNotifications();
  const [createRelationship] = useMutation<
    CreateCompanyRelationshipMutation,
    CreateCompanyRelationshipMutationVariables
  >(createCompanyRelationshipMutation);
  const [selectedMode, setSelectedMode] = useState<Mode>('new_hub');
  const [selectedCompany, setSelectedCompany] = useState<CompanySearchResult | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingLink, setPendingLink] = useState<PendingLink | null>(null);

  const mode: Mode = role === 'hub' ? 'new_hub' : selectedMode;

  const submit = async () => {
    if (selectedCompany === null) {
      return;
    }

    setIsSaving(true);
    try {
      const selectedCompanyName = selectedCompany.company_name ?? selectedCompany.company_id;
      const selectedRole = await fetchCompanyGroupRole(apolloClient, selectedCompany.company_id);
      const validationError = validateSelection(mode, selectedRole, selectedCompanyName);
      if (validationError !== undefined) {
        notify.error({ title: 'Cannot add to company group', message: validationError });
        return;
      }

      setPendingLink(
        mode === 'new_hub'
          ? {
              sourceCompanyId: selectedCompany.company_id,
              targetCompanyId: companyId,
              memberName: selectedCompanyName,
              hubName: companyName,
            }
          : {
              sourceCompanyId: companyId,
              targetCompanyId: selectedCompany.company_id,
              memberName: companyName,
              hubName: selectedCompanyName,
            },
      );
    } catch {
      notify.error({ title: 'Failed to check the selected company' });
    } finally {
      setIsSaving(false);
    }
  };

  const confirm = async () => {
    if (pendingLink === null) {
      return;
    }

    setIsSaving(true);
    try {
      const { sourceCompanyId, targetCompanyId } = pendingLink;
      const { data } = await createRelationship({
        variables: {
          input: { sourceCompanyId, targetCompanyId, relationshipType: CompanyRelationshipType.COMPANY_GROUP },
        },
      });

      if (isNil(data?.createCompanyRelationship)) {
        notify.error({
          title: 'Company group link was not created',
          message: 'It was rejected or is still being processed. Reload the page in a moment to check.',
        });
        return;
      }

      notify.success({ title: 'Company group link created' });
      setSelectedCompany(null);
      onCreated();
    } catch {
      notify.error({ title: 'Failed to create company group link' });
    } finally {
      setIsSaving(false);
      setPendingLink(null);
    }
  };

  return (
    <Stack direction="column" gap="small">
      {role === 'none' && (
        <RadioGroup
          name="company_group_mode"
          label="Company group"
          value={mode}
          onChange={({ value }) => {
            setSelectedMode(value === 'existing_hub' ? 'existing_hub' : 'new_hub');
            setSelectedCompany(null);
          }}>
          <Radio value="new_hub" label="Make this company the hub of a new company group" />
          <Radio value="existing_hub" label="Add this company to an existing hub" />
        </RadioGroup>
      )}
      <Text size="small">
        {mode === 'new_hub'
          ? 'Choose a company to add as a member of this company group.'
          : 'Choose the hub to link this company to.'}
      </Text>
      <Stack direction="row" gap="small" alignItems="center">
        <div style={{ minWidth: 360 }}>
          <CompaniesDropdownContainer
            inputProps={{ placeholder: mode === 'new_hub' ? 'Member company' : 'Hub company', disabled: isSaving }}
            selectedCompany={selectedCompany}
            onSelectCompany={setSelectedCompany}
          />
        </div>
        <Button
          kind="primary"
          size="small"
          loading={isSaving}
          disabled={selectedCompany === null || selectedCompany.company_id === companyId}
          onClick={submit}>
          {getSubmitLabel(role, mode)}
        </Button>
      </Stack>
      <CompanyGroupLinkConfirmModal
        open={pendingLink !== null}
        memberName={pendingLink?.memberName ?? ''}
        hubName={pendingLink?.hubName ?? ''}
        isSaving={isSaving}
        onCancel={() => setPendingLink(null)}
        onConfirm={confirm}
      />
    </Stack>
  );
};

function getSubmitLabel(role: CompanyGroupFormProps['role'], mode: Mode): string {
  if (role === 'hub') {
    return 'Add member';
  }
  switch (mode) {
    case 'new_hub':
      return 'Create Company Group';
    case 'existing_hub':
      return 'Join Hub';
    default: {
      const exhaustiveCheck: never = mode;
      throw new Error(`Unknown company group mode: ${exhaustiveCheck}`);
    }
  }
}

function validateSelection(mode: Mode, selectedRole: CompanyGroupRole, companyName: string): string | undefined {
  switch (mode) {
    case 'new_hub':
      return selectedRole === 'none' ? undefined : `${companyName} is already in a company group.`;
    case 'existing_hub':
      return selectedRole === 'hub' ? undefined : `${companyName} is not the hub of a company group.`;
    default: {
      const exhaustiveCheck: never = mode;
      throw new Error(`Unknown company group mode: ${exhaustiveCheck}`);
    }
  }
}
